/**
 * areaIdentityStartFlow — PURE orchestration of the resumable-area
 * "start / resume identity verification" action (PR-ONB-PREAUTH-4).
 *
 * Mirrors the token-freshness contract of `areaSubmitFlow`: the candidate may
 * have verified their email in ANOTHER tab, so before starting we reload the
 * Firebase user, force a FRESH ID token, re-read /area/me with that token, and
 * POST the start with the SAME token. On success the caller navigates the
 * browser to `redirectUrl` (when non-null).
 *
 * Generic over its dependencies (no fetch, no Firebase import, no
 * window.location) so the call order + token reuse + the redirect decision are
 * unit-testable without rendering React or navigating.
 */

import type { AreaFailure, AreaIdentityStartOk, AreaMe } from '@/lib/areaApi';

export interface AreaIdentityStartDeps {
  /** firebaseUser.reload() — refreshes the local emailVerified claim. */
  reloadUser: () => Promise<void>;
  /** getIdToken(force). MUST be called with force=true here. */
  getToken: (force: boolean) => Promise<string | null>;
  /** GET /area/me with the given token. */
  getMe: (token: string) => Promise<{ ok: true; data: AreaMe } | AreaFailure>;
  /** POST /area/request/identity-verification/start with the given token. */
  start: (
    token: string,
  ) => Promise<{ ok: true; data: AreaIdentityStartOk } | AreaFailure>;
}

export type AreaIdentityStartFlowResult =
  | { kind: 'REDIRECT'; url: string; data: AreaIdentityStartOk }
  | { kind: 'NO_REDIRECT'; data: AreaIdentityStartOk }
  | { kind: 'NO_SESSION' }
  | { kind: 'ME_UNAVAILABLE' }
  | { kind: 'NOT_PREAUTHORIZED' }
  | { kind: 'EMAIL_NOT_VERIFIED' }
  | { kind: 'START_FAILED'; failure: AreaFailure };

export async function performAreaIdentityStart(
  deps: AreaIdentityStartDeps,
): Promise<AreaIdentityStartFlowResult> {
  // 1 + 2. Reload the Firebase user THEN force a fresh token, so an email
  // verified in another tab is reflected in the claim this start carries.
  await deps.reloadUser();
  const token = await deps.getToken(true);
  if (!token) return { kind: 'NO_SESSION' };

  // 3. Re-read /area/me with the fresh token (status + email).
  const me = await deps.getMe(token);
  if (!me.ok || !me.data.request) return { kind: 'ME_UNAVAILABLE' };
  if (!me.data.account.emailVerified) return { kind: 'EMAIL_NOT_VERIFIED' };
  // Client-side guard — the BE remains the authority (409 NOT_PREAUTHORIZED).
  if (me.data.request.status !== 'PREAUTHORIZED_FOR_IDENTITY') {
    return { kind: 'NOT_PREAUTHORIZED' };
  }

  // 4. Start / resume with the SAME fresh token.
  const result = await deps.start(token);
  if (!result.ok) return { kind: 'START_FAILED', failure: result };
  if (result.data.redirectUrl) {
    return { kind: 'REDIRECT', url: result.data.redirectUrl, data: result.data };
  }
  return { kind: 'NO_REDIRECT', data: result.data };
}
